import { FC, ReactNode, useEffect, useMemo, useContext, useState } from 'react';
import { useSnapshot } from 'valtio';
import { Entity } from '#types/System.types';
import { HOME_PAGE_QUERY_LIMIT } from '#types/System.Parameters.types';
import { createCollectionStore } from '../../store';
import { EntitiesContext } from './EntitiesContext';
import { UserContext } from '../UserContext';

interface EntitiesProviderProps {
  children: ReactNode;
}

const entitiesStore = createCollectionStore<Entity>('entities', {
  queryOptions: { extraConstraints: [{ type: 'limit', n: HOME_PAGE_QUERY_LIMIT }] },
});

export const EntitiesProvider: FC<EntitiesProviderProps> = ({ children }) => {
  const { isLoggedIn, loading: authLoading } = useContext(UserContext);
  const [error, setError] = useState<Error | null>(null);

  const isActive = isLoggedIn && !authLoading;

  useEffect(() => {
    if (!isActive) return;
    setError(null);
    entitiesStore.connect({ onError: (e) => setError(e) });
    return () => entitiesStore.disconnect();
  }, [isActive]);

  const snap = useSnapshot(entitiesStore.state);

  const entitiesArray = useMemo<Entity[]>(() => {
    if (!isActive) return [];
    const result: Entity[] = [];
    for (const id of snap.docIds) {
      const entry = snap.docs.get(id);
      if (entry) result.push({ ...(entry.data as Entity), docId: id });
    }
    return result;
  }, [isActive, snap.docIds, snap.docs]);

  const entities = useMemo(() =>
    entitiesArray.reduce((acc, entity) => {
      if (entity.docId) {
        acc[entity.docId] = entity;
      }
      return acc;
    }, {} as Record<string, Entity>),
  [entitiesArray]);

  const loading = isActive ? snap.loading : false;

  const contextValue = useMemo(
    () => ({
      entities,
      entitiesArray,
      isLoading: loading,
      error,
    }),
    [entities, entitiesArray, loading, error]
  );

  return (
    <EntitiesContext.Provider value={contextValue}>
      {children}
    </EntitiesContext.Provider>
  );
};
